import { App, type AppPlugin } from '@capacitor/app'
import { type ConnectionStatus, Network } from '@capacitor/network'
import { useAppMode } from '~/composables/app'

/**
 * @description Composable to access the Capacitor App plugin.
 * Useful for accessing appInfo, exitApp, launchUrl, minimiseApp etc.
 * @see https://ionicframework.com/docs/native/app
 * @returns {AppPlugin | undefined}
 */
export function useApp(): AppPlugin | undefined {
  const { isWebMode } = useAppMode()

  if (isWebMode || !isPlatform('capacitor')) {
    return
  }

  return App
}

/**
 * @description Gets the app version and build number
 * @returns {Promise<string>} e.g. 1.0.0 (1)
 */
export const getAppVersion = async (): Promise<string | undefined> => {
  const { isWebMode } = useAppMode()

  // If Web Mode or mobile development in desktop browser, return undefined
  if (isWebMode || !isPlatform('capacitor')) {
    const version = import.meta.env.VITE_APP_VERSION
    const build = import.meta.env.VITE_APP_BUILD
    return version && build ? `${version} (${build})` : undefined
  }

  // If VITE DEV mode, return the version from package.json
  if (import.meta.env.DEV) {
    const devVersion = (await import('../../package.json')).version
    return `${devVersion} (DEV)`
  }

  // Otherwise return the version from the App plugin
  const appInfo = await App.getInfo()

  return `${appInfo.version} (${appInfo.build})`
}

/**
 * @description Composable to access the Capacitor Network plugin.
 * Useful for accessing networkStatusChange, getStatus, isAvailable etc.
 *
 */
export function useNetworkStatus() {
  const networkStatus = ref<ConnectionStatus>()
  const isConnected = ref<boolean>(false)

  onBeforeMount(async () => {
    const initialStatus = await Network.getStatus()
    networkStatus.value = initialStatus
    isConnected.value = initialStatus.connected
  })

  Network.addListener('networkStatusChange', (status) => {
    networkStatus.value = status
    isConnected.value = status.connected
  })

  async function clearNetworkListener() {
    await Network.removeAllListeners()
  }

  return {
    networkStatus: readonly(networkStatus),
    isConnected: readonly(isConnected),
    clearNetworkListener
  }
}
